/**
 * AI Preview Pane
 *
 * Shows preview of AI-generated presentation screens.
 */

import React from 'react';
import { useAI } from './context/AIContext';
import { ScreenContent, ContentBlock } from '../../ai/schemas/ContentBlock';

interface AIPreviewPaneProps {
  showThumbnails?: boolean;
}

export function AIPreviewPane({ showThumbnails = true }: AIPreviewPaneProps) {
  const {
    presentation,
    selectedScreenIndex,
    setSelectedScreenIndex,
    selectedTheme,
  } = useAI();

  if (!presentation) {
    return (
      <div className="ai-preview-empty">
        <div className="ai-preview-placeholder">
          <svg viewBox="0 0 24 24" width="48" height="48">
            <path
              fill="currentColor"
              d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"
            />
            <path
              fill="currentColor"
              d="M7.5 13h2v4h-2zm4.5-3h2v7h-2zm4.5 5h2v2h-2z"
            />
          </svg>
          <p>Generate a presentation to see preview</p>
        </div>
      </div>
    );
  }

  const currentScreen = presentation.screens[selectedScreenIndex];

  return (
    <div className="ai-preview-pane">
      {showThumbnails && presentation.screens.length > 1 && (
        <div className="ai-preview-thumbnails">
          {presentation.screens.map((screen, index) => (
            <button
              key={screen.id}
              className={`ai-thumbnail ${index === selectedScreenIndex ? 'selected' : ''}`}
              onClick={() => setSelectedScreenIndex(index)}
              style={{
                backgroundColor: selectedTheme.colors.background,
              }}
            >
              <span className="ai-thumbnail-number">{index + 1}</span>
              <span className="ai-thumbnail-type">{formatScreenType(screen.screenType)}</span>
            </button>
          ))}
        </div>
      )}

      <div
        className="ai-preview-main"
        style={{
          backgroundColor: selectedTheme.colors.background,
          color: selectedTheme.colors.textPrimary,
        }}
      >
        <div className="ai-preview-screen">
          {currentScreen && <ScreenPreview screen={currentScreen} theme={selectedTheme} />}
        </div>

        <div className="ai-preview-nav">
          <button
            disabled={selectedScreenIndex === 0}
            onClick={() => setSelectedScreenIndex(selectedScreenIndex - 1)}
          >
            Previous
          </button>
          <span>
            {selectedScreenIndex + 1} / {presentation.screens.length}
          </span>
          <button
            disabled={selectedScreenIndex === presentation.screens.length - 1}
            onClick={() => setSelectedScreenIndex(selectedScreenIndex + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Format screen type for display
 */
function formatScreenType(type: string): string {
  return type
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Screen Preview component
 */
function ScreenPreview({
  screen,
  theme,
}: {
  screen: ScreenContent;
  theme: { colors: { textPrimary: string; textSecondary: string; primary: string } };
}) {
  return (
    <div className="ai-screen-preview">
      {screen.blocks.map((block) => (
        <BlockPreview key={block.id} block={block} theme={theme} />
      ))}
    </div>
  );
}

/**
 * Block Preview component
 */
function BlockPreview({
  block,
  theme,
}: {
  block: ContentBlock;
  theme: { colors: { textPrimary: string; textSecondary: string; primary: string } };
}) {
  switch (block.type) {
    case 'title':
      return (
        <h1
          className="ai-block-title"
          style={{
            color: theme.colors.textPrimary,
            fontSize: block.level === 1 ? '2.5rem' : block.level === 2 ? '2rem' : '1.5rem',
          }}
        >
          {block.text}
        </h1>
      );

    case 'subtitle':
      return (
        <h2
          className="ai-block-subtitle"
          style={{ color: theme.colors.textSecondary }}
        >
          {block.text}
        </h2>
      );

    case 'body':
      return (
        <p
          className="ai-block-body"
          style={{
            color: theme.colors.textPrimary,
            opacity: block.emphasis === 'subtle' ? 0.7 : 1,
            fontWeight: block.emphasis === 'strong' ? 600 : 400,
          }}
        >
          {block.text}
        </p>
      );

    case 'bullet-list':
      return (
        <ul className="ai-block-list">
          {block.items.map((item, i) => (
            <li key={i} style={{ color: theme.colors.textPrimary }}>
              {item}
            </li>
          ))}
        </ul>
      );

    case 'numbered-list':
      return (
        <ol className="ai-block-list" start={block.startNumber || 1}>
          {block.items.map((item, i) => (
            <li key={i} style={{ color: theme.colors.textPrimary }}>
              {item}
            </li>
          ))}
        </ol>
      );

    case 'quote':
      return (
        <blockquote className="ai-block-quote">
          <p style={{ color: theme.colors.textPrimary }}>"{block.text}"</p>
          {block.attribution && (
            <cite style={{ color: theme.colors.textSecondary }}>
             , {block.attribution}
            </cite>
          )}
        </blockquote>
      );

    case 'callout':
      return (
        <div
          className={`ai-block-callout ai-callout-${block.calloutType || 'info'}`}
          style={{ borderColor: theme.colors.primary }}
        >
          <p>{block.text}</p>
        </div>
      );

    case 'image-placeholder':
      return (
        <div className="ai-block-image-placeholder">
          <svg viewBox="0 0 24 24" width="32" height="32">
            <path
              fill="currentColor"
              d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
            />
          </svg>
          <span>{block.description}</span>
        </div>
      );

    case 'speaker-notes':
      return null; // Don't show speaker notes in preview

    default:
      return null;
  }
}

export default AIPreviewPane;
